import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Link2, FileCheck2, Users as UsersIcon, GitBranch } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const Section = ({ icon: Icon, title, children }) => (
  <Card className="border-border">
    <CardContent className="p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
        <h2 className="font-display text-lg font-bold">{title}</h2>
      </div>
      <div className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">{children}</div>
    </CardContent>
  </Card>
);

const Code = ({ children }) => (
  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px] text-foreground">{children}</code>
);

export default function Documentacion() {
  return (
    <div data-theme="institutional">
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary"><FileCheck2 className="h-4 w-4" /> Documentación técnica</span>
          <h1 className="mt-1 font-display text-3xl font-bold">Cómo funciona el sorteo verificable</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">Descripción del algoritmo provably-fair, el modelo de amenazas y el procedimiento de verificación independiente.</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-4xl gap-5 px-4 py-10 sm:px-6 lg:px-10">
        <Section icon={Lock} title="1. Compromiso previo (commit)">
          <p>Antes de cada sorteo, el sistema genera una <strong>semilla del servidor</strong> con un generador criptográficamente seguro (CSPRNG, <Code>secrets.token_hex</Code>) y publica su <strong>compromiso</strong>: <Code>SHA-256(server_seed)</Code>. Esto fija la semilla sin revelarla, impidiendo que se altere después.</p>
        </Section>
        <Section icon={GitBranch} title="2. Derivación del resultado (provably-fair)">
          <p>El resultado se deriva de forma determinística combinando tres entradas:</p>
          <p><Code>HMAC-SHA256(server_seed, "{'{'}client_seed{'}'}:{'{'}nonce{'}'}:{'{'}contador{'}'}")</Code></p>
          <p>La <strong>semilla del cliente</strong> aporta entropía que el operador no controla, y el <strong>nonce</strong> hace único cada sorteo. El flujo de bytes resultante se convierte en números con <strong>muestreo por rechazo</strong> (rejection sampling) para evitar sesgo de módulo, garantizando uniformidad.</p>
        </Section>
        <Section icon={ShieldCheck} title="3. Revelación y verificación">
          <p>Tras el sorteo se <strong>revela la semilla del servidor</strong>. Cualquiera puede: (a) comprobar que <Code>SHA-256(server_seed)</Code> coincide con el compromiso publicado; (b) recomputar el resultado con (server_seed, client_seed, nonce). Si todo coincide, el sorteo es auténtico e inalterado. Usa el <Link to="/verificar" className="text-primary underline">portal de verificación</Link>.</p>
        </Section>
        <Section icon={Link2} title="4. Bitácora inmutable (hash encadenado)">
          <p>Cada sorteo se registra en una cadena donde <Code>record_hash = SHA-256(prev_hash + json_canónico(record))</Code>. Modificar cualquier registro rompe todos los hashes siguientes, haciendo la manipulación detectable. Consulta la <Link to="/bitacora" className="text-primary underline">bitácora</Link>.</p>
        </Section>
        <Section icon={FileCheck2} title="5. Acta y paquete de auditoría">
          <p>Cada sorteo genera un <strong>acta PDF</strong> y un <strong>paquete ZIP de auditoría</strong> que incluye el acta, el sorteo en JSON canónico, la prueba de cadena y un instructivo de verificación independiente.</p>
        </Section>
        <Section icon={UsersIcon} title="6. Roles y control de acceso">
          <p><strong>Administrador:</strong> gestiona usuarios, juegos y sorteos. <strong>Operador:</strong> crea, ejecuta y revela sorteos. <strong>Auditor/Veedor:</strong> acceso de solo lectura. El sitio público y la verificación son abiertos; las operaciones requieren autenticación (JWT) y rol autorizado. Los estados del sorteo siguen una máquina estricta: <Code>comprometido → ejecutado → revelado</Code>, sin reversa.</p>
        </Section>
      </section>
    </div>
  );
}
